/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/core';
import { Animation, Colors, Heading, HeadingLevel, Icon, Typography, Shadows, } from '@zapier/design-system';
import { createKeyHandler } from '@zapier/product-get-help/esm/utils';
import { isolateComponent } from '@zapier/style-encapsulation';
import Zap from '../Zap';
const folderCss = css `
  border: 1px solid ${Colors.neutral200};
  background-color: ${Colors.neutral200};
  color: ${Colors.neutral800};
  padding: 0 10px;
`;
const openFolderCss = css `
  ${folderCss}
  background-color: ${Colors.neutral100};
  border-color: ${Colors.neutral400};
`;
const toggleableHighlightCss = css `
  box-shadow: ${Shadows.elevation10};
  border-color: ${Colors.neutral400};
  border-radius: 10px;
  background-color: ${Colors.neutral100};
  color: ${Colors.blueJeans};
`;
const closedToggleableFolderCss = css `
  &:hover {
    ${toggleableHighlightCss};
  }
`;
const toggleableFolderCss = (isOpen) => css `
  ${folderCss}
  transition-property: box-shadow border-color background-color color;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  border-radius: ${'10px'};
  position: relative;
  &::before {
    content: '';
    position: absolute;
    pointer-events: none;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    border: 3px solid transparent;
    border-radius: 14px;
    transition-property: all;
    transition-timing-function: ${Animation.transitionTimingFunction};
    transition-duration: ${Animation.transitionDuration};
  }
  &:focus {
    ${toggleableHighlightCss};
  }
  &:focus::before {
    border-color: ${Colors.blueJeans};
    top: -6px;
    right: -6px;
    bottom: -6px;
    left: -6px;
  }
  ${isOpen ? openFolderCss : closedToggleableFolderCss}
`;
const headingCss = css `
  ${Typography.smallPrint1Bold}
  height: 50px;
  display: grid;
  grid-column-gap: 15px;
  align-items: center;
  grid-template-columns: min-content 1fr;
`;
const toggleableHeadingCss = () => css `
  ${headingCss}
  grid-template-columns: min-content 1fr min-content;
  cursor: pointer;
  &:hover {
    color: ${Colors.blueJeans};
  }
`;
const zapsCss = css `
  display: grid;
  grid-row-gap: 5px;
  margin-bottom: 10px;
`;
const Zaps = ({ isOpen, onSelect, zaps }) => {
    if (!isOpen) {
        return null;
    }
    return (isolateComponent("ul", { css: zapsCss },
        isolateComponent(HeadingLevel, null, zaps.map((zap, i) => (isolateComponent(Zap, Object.assign({ key: i }, zap, { onClick: () => onSelect(zap) })))))));
};
/** Renders a single folders within `<FolderGroup>`. */
const Folder = ({ forceOpen, onSelect, title, zaps }) => {
    const [isToggledOpen, setIsToggledOpen] = useState(false);
    if (forceOpen) {
        return (isolateComponent("li", null,
            isolateComponent("div", { css: openFolderCss },
                isolateComponent("div", { css: headingCss },
                    isolateComponent(Icon, { name: "navFolder", size: 24 }),
                    isolateComponent(Heading, null, title)),
                isolateComponent(Zaps, { isOpen: true, onSelect: onSelect, zaps: zaps }))));
    }
    const onToggle = () => setIsToggledOpen(!isToggledOpen);
    const isOpen = forceOpen || isToggledOpen;
    return (isolateComponent("li", null,
        isolateComponent("div", { css: toggleableFolderCss(isOpen), onClick: onToggle, onKeyDown: createKeyHandler(['enter'], onToggle), role: "button", tabIndex: 0 },
            isolateComponent("div", { css: toggleableHeadingCss() },
                isolateComponent(Icon, { name: "navFolder", size: 24 }),
                isolateComponent(Heading, null, title),
                isolateComponent(Icon, { name: isOpen ? 'arrowSmallUp' : 'arrowSmallDown', size: 24 })),
            isolateComponent(Zaps, { isOpen: isOpen, onSelect: onSelect, zaps: zaps }))));
};
export default Folder;
